import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, User, Lock, AlertCircle, LogIn, Loader, Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import api from '../../config/api';

export default function NGOLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      // Validate form
      if (!formData.identifier || !formData.password) {
        setErrorMessage('Please enter both identifier and password');
        toast.error('Please fill in all fields');
        setIsSubmitting(false);
        return;
      }

      console.log('Attempting NGO login with:', formData.identifier);
      console.log('API base URL:', api.defaults.baseURL);
      console.log('Full endpoint:', `${api.defaults.baseURL}/ngos/login`);

      // Use the configured API endpoint
      const response = await api.post('/ngos/login', formData);

      console.log('Login successful:', response.data);

      // Store token and user info in localStorage with consistent naming
      localStorage.setItem('ngoToken', response.data.token);
      localStorage.setItem('ngoUser', JSON.stringify(response.data.ngo));
      localStorage.setItem('userType', 'ngo');

      // Set token in axios default headers
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      toast.success('Login successful!');

      // Redirect to NGO dashboard
      navigate('/ngo/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);

      let errorMsg = 'Login failed. Please try again.';

      if (error.response) {
        // Server responded with error status
        if (error.response.status === 404) {
          errorMsg = 'API endpoint not found. Please check if the server is running.';
        } else if (error.response.status === 500) {
          errorMsg = 'Server error. Please try again later.';
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMsg = 'No response from server. Please check your connection and if the server is running.';
      } else {
        // Something else happened
        errorMsg = error.message || 'An unexpected error occurred.';
      }

      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center py-6">
      <div className="w-full max-w-sm mx-auto px-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6"
        >
          <div className="text-center mb-5">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Building2 className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">NGO Login</h2>
            <p className="text-sm text-gray-600">Manage your NGO campaigns</p>
          </div>

          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs mb-4"
            >
              <div className="flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5 w-4 h-4 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-800">Login Failed</h3>
                  <p className="text-red-700 mt-0.5">{errorMessage}</p>
                </div>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="identifier" className="block text-xs font-semibold text-gray-700 mb-1.5">
                Email or Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="identifier"
                  type="text"
                  name="identifier"
                  required
                  value={formData.identifier}
                  onChange={handleChange}
                  className="pl-9 pr-3 py-2 block w-full border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  placeholder="Enter email or phone"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-semibold text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-9 pr-10 py-2 block w-full border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs font-semibold text-red-600 hover:text-red-700 transition-colors">
                Forgot password?
              </Link>
            </div>

            <motion.button
              type="submit"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              disabled={isSubmitting}
              className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-lg shadow-md text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Logging in...
                </>
              ) : (
                <>
                  Login
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white text-gray-400 font-medium">New to BloodHero?</span>
              </div>
            </div>

            <div className="mt-4">
              <Link
                to="/ngo-registration"
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-red-200 rounded-lg shadow-sm text-sm font-semibold text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
              >
                <Building2 className="w-3.5 h-3.5" />
                Register NGO Account
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}